import {
    Box, Button, Checkbox, Chip, Dialog,
    DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, ListItemText,
    MenuItem, Paper, Select, Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import GroupsIcon from "@mui/icons-material/Groups";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";

import {
    getOrganization,
    updateSharedFarms,
    leaveOrganization,
    deleteOrganization,
} from "../../api/organizations";

export default function OrganizationModal({
    open,
    onClose,
    organization,
    farms = [],
    translate,
    onRefresh,
}) {
    const [loading, setLoading] = useState(false);
    const [members, setMembers] = useState([]);
    const [sharedFarmIds, setSharedFarmIds] = useState([]);
    const [confirmAction, setConfirmAction] = useState(null);

    const isOwner =
        organization?.role === "owner";

    const allFarmIds = useMemo(
        () => farms.map(f => f.farm_id),
        [farms]
    );

    useEffect(() => {
        if (!open || !organization) {
            return;
        }

        async function load() {
            try {
                setLoading(true);

                const data =
                    await getOrganization(
                        organization.organization_id
                    );

                const normalizedMembers =
                    Array.isArray(data?.members?.[0])
                        ? data.members[0]
                        : (data?.members ?? []);

                const normalizedSharedFarms =
                    Array.isArray(data?.shared_farms?.[0])
                        ? data.shared_farms[0]
                        : (data?.shared_farms ?? []);

                setMembers(normalizedMembers);

                setSharedFarmIds(
                    normalizedSharedFarms.map(
                        farm => farm.farm_id
                    )
                );
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }

        load();
    }, [open, organization]);

    const handleFarmChange = async (nextFarmIds) => {
        try {
            setSharedFarmIds(nextFarmIds);

            await updateSharedFarms(
                organization.organization_id,
                nextFarmIds
            );
        } catch (error) {
            console.error(error);
        }
    };

    const handleAllFarms = async () => {
        await handleFarmChange(
            sharedFarmIds.length === allFarmIds.length
                ? []
                : allFarmIds
        );
    };

    const handleLeave = async () => {
        try {
            setLoading(true);

            await leaveOrganization(
                organization.organization_id
            );

            setConfirmAction(null);
            onClose();
            onRefresh?.();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setLoading(true);

            await deleteOrganization(
                organization.organization_id
            );

            setConfirmAction(null);
            onClose();
            onRefresh?.();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    if (!organization) {
        return null;
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: {
                        borderRadius: 5,
                        maxHeight: "85vh",
                    },
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}>
                        <Box>
                            <Typography sx={{
                                fontFamily: "Fraunces",
                                fontSize: 28,
                                color: "var(--color-text-primary)",
                            }}>
                                {organization.organization_name}
                            </Typography>

                            <Typography sx={{
                                color: "text.secondary",
                            }}>
                                {translate(organization.role)}
                            </Typography>
                        </Box>

                        {!!organization.invite_code && (
                            <Chip
                                label={organization.invite_code}
                                sx={{ borderRadius: 3 }}
                            />
                        )}
                    </Box>
                </DialogTitle>

                <DialogContent
                    dividers
                    sx={{
                        overflowY: "auto",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 4,
                            backgroundColor:
                                "var(--color-green-soft)",
                        }}
                    >
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                        }}>
                            <GroupsIcon />

                            <Typography sx={{
                                fontWeight: 700,
                            }}>
                                {translate("members")}
                            </Typography>
                        </Box>

                        {!members.length && !loading && (
                            <Typography sx={{
                                color: "text.secondary",
                            }}>
                                {translate("no_members")}
                            </Typography>
                        )}

                        <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                        }}>
                            {members.map(member => (
                                <Box
                                    key={member.user_id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 1.5,
                                        borderRadius: 3,
                                        backgroundColor:
                                            "rgba(255,255,255,0.45)",
                                    }}
                                >
                                    <Typography>
                                        {member.first_name} {member.last_name}
                                    </Typography>

                                    <Chip
                                        size="small"
                                        label={translate(member.role)}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Paper>

                    {!!farms.length && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 4,
                                backgroundColor:
                                    "var(--color-green-soft)",
                            }}
                        >
                            <Box sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                            }}>
                                <Typography sx={{
                                    fontWeight: 700,
                                }}>
                                    {translate("share_farm_data")}
                                </Typography>

                                <Button
                                    variant="outlined"
                                    onClick={handleAllFarms}
                                    sx={{
                                        borderRadius: 3,
                                        textTransform: "none",
                                    }}
                                >
                                    {translate("all")}
                                </Button>
                            </Box>

                            <FormControl fullWidth>
                                <InputLabel>
                                    {translate("farms")}
                                </InputLabel>

                                <Select
                                    multiple
                                    value={sharedFarmIds}
                                    label={translate("farms")}
                                    onChange={e =>
                                        handleFarmChange(
                                            e.target.value
                                        )
                                    }
                                    renderValue={selected =>
                                        farms
                                            .filter(f =>
                                                selected.includes(
                                                    f.farm_id
                                                )
                                            )
                                            .map(f =>
                                                f.farm_name
                                            )
                                            .join(", ")
                                    }
                                >
                                    {farms.map(farm => (
                                        <MenuItem
                                            key={farm.farm_id}
                                            value={farm.farm_id}
                                        >
                                            <Checkbox
                                                checked={
                                                    sharedFarmIds.includes(
                                                        farm.farm_id
                                                    )
                                                }
                                            />

                                            <ListItemText
                                                primary={
                                                    farm.farm_name
                                                }
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Paper>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={onClose}>
                        {translate("cancel")}
                    </Button>

                    <Button
                        color="error"
                        variant={
                            isOwner
                                ? "contained"
                                : "outlined"
                        }
                        disabled={loading}
                        startIcon={
                            isOwner
                                ? <DeleteOutlineIcon />
                                : <ExitToAppIcon />
                        }
                        onClick={() =>
                            setConfirmAction(
                                isOwner
                                    ? "delete"
                                    : "leave"
                            )
                        }
                        sx={{
                            borderRadius: 3,
                            textTransform: "none",
                        }}
                    >
                        {translate(
                            isOwner
                                ? "delete_organization"
                                : "leave_organization"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!confirmAction}
                onClose={() =>
                    setConfirmAction(null)
                }
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    {translate("are_you_sure")}
                </DialogTitle>

                <DialogContent>
                    <Typography>
                        {translate(
                            confirmAction === "delete"
                                ? "delete_organization_warning"
                                : "leave_organization_warning"
                        )}
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() =>
                            setConfirmAction(null)
                        }
                    >
                        {translate("cancel")}
                    </Button>

                    <Button
                        color="error"
                        variant="contained"
                        disabled={loading}
                        onClick={
                            confirmAction === "delete"
                                ? handleDelete
                                : handleLeave
                        }
                    >
                        {translate("confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}